/**
 * Table column catalogs and saved layouts (docs/00 Tier 1 via ADR-23; docs/03
 * §3; docs/04 §5).
 *
 * Compact density earns its keep only when the user also controls WHICH columns
 * are on screen — a dense table you cannot choose the columns of just shows you
 * more of the wrong thing. So every list table declares a catalog here, and a
 * user's chosen subset is stored server-side (`user_preferences.saved_layouts`),
 * which is what makes the choice follow them across sessions, browsers and
 * machines rather than living in one browser's localStorage.
 *
 * The catalog lives in `shared` because BOTH sides need it: the web app renders
 * from it, and the API validates against it. Without server-side validation a
 * crafted request could store unknown column ids or drop a locked column, and
 * the next page load would render a table with no identifying column at all.
 *
 * Column ORDER is the catalog's order. Reordering and widths stay Tier 2
 * (ADR-23) — deliberately, because they need drag affordances and a per-column
 * width store, and neither is what makes a dense table usable.
 */

import { z } from 'zod';

export interface TableColumnDef {
  /** Stable id. Persisted, so it is renamed only with a migration of layouts. */
  readonly id: string;
  readonly label: string;
  /**
   * Always visible. The selector renders it checked and disabled with a reason,
   * and the server forces it back on — a table whose rows cannot be identified
   * is not a table anybody can use.
   */
  readonly locked?: true;
  /** Visible for a user who has never chosen. */
  readonly defaultOn?: true;
  /** Server sort key (docs/08 whitelist), when this column is sortable. */
  readonly sortKey?: string;
}

/**
 * Properties list (defaults agreed with Matt 2026-08-22): name locked, then
 * city, type, square footage and status on; everything else available but off.
 *
 * "City" is a location cell — it renders `city, state` — while the separate
 * State column exists for people who want state on its own to sort or scan by.
 * That is mild duplication, and the alternative was for the default view to
 * silently lose the state it shows today.
 *
 * The favourite star is deliberately NOT in this catalog. It is the affordance
 * for favouriting, not data about the property, and a user who switched it off
 * would be left wondering why they can no longer favourite anything.
 */
const PROPERTY_COLUMNS: readonly TableColumnDef[] = [
  { id: 'name', label: 'Name', locked: true, sortKey: 'name' },
  /**
   * Address SECOND, and on by default (docs/17 3D.5; Matt, 2026-08-28).
   *
   * It was off entirely, which the Yardi load made untenable: most property
   * names are the owning legal entity — `TKC CIII, LLC`, `JBJ Greensboro,
   * LLC` — and a list of LLCs tells nobody which building is which. The
   * address does, so it sits next to the name rather than eleventh and
   * switched off.
   *
   * Order in this array IS the display order, and it is the DEFAULT only.
   * Anybody who has already arranged this table keeps their arrangement —
   * `saved_layouts` wins, which is the whole point of having saved it.
   */
  { id: 'address', label: 'Address', defaultOn: true },
  // Photo is on by default: TKC wants photos, and a column nobody has switched
  // on is a feature nobody sees. It renders at `thumb` and shrinks with the
  // density tokens, which is what "scale them down in compact" means.
  { id: 'photo', label: 'Photo', defaultOn: true },
  { id: 'city', label: 'City', defaultOn: true, sortKey: 'city' },
  { id: 'type', label: 'Type', defaultOn: true, sortKey: 'type' },
  { id: 'sqFt', label: 'Sq ft', defaultOn: true, sortKey: 'sq_ft' },
  { id: 'status', label: 'Status', defaultOn: true, sortKey: 'status' },
  { id: 'state', label: 'State' },
  { id: 'postalCode', label: 'Postal code' },
  { id: 'yearBuilt', label: 'Year built', sortKey: 'year_built' },
  { id: 'managementType', label: 'Management type', sortKey: 'management_type' },
  { id: 'group', label: 'Group' },
  { id: 'yardiId', label: 'Yardi ID' },
  { id: 'tkcNumber', label: 'TKC number', sortKey: 'tkc_number' },
  { id: 'updatedAt', label: 'Last updated', sortKey: 'updated_at' },
];

/**
 * Every table with a selector. The key is the layout key persisted in
 * `saved_layouts`, so it is part of the stored contract: renaming one orphans
 * everybody's saved choice for that table.
 */
/** Tenant organizations list (ADR-06). */
const TENANT_ORG_COLUMNS: readonly TableColumnDef[] = [
  { id: 'name', label: 'Name', locked: true, sortKey: 'name' },
  { id: 'industry', label: 'Industry', defaultOn: true, sortKey: 'industry' },
  { id: 'activeTenancies', label: 'Active tenancies', defaultOn: true },
  { id: 'status', label: 'Status', defaultOn: true, sortKey: 'status' },
  { id: 'updatedAt', label: 'Last updated', sortKey: 'updated_at' },
];

/** Vendors list (ADR-04). COI expiration is on by default: the compliance
 * date is the column this list exists to make visible. */
const VENDOR_COLUMNS: readonly TableColumnDef[] = [
  { id: 'name', label: 'Name', locked: true, sortKey: 'name' },
  { id: 'vendorType', label: 'Type', defaultOn: true, sortKey: 'vendor_type' },
  // Not sortable: a vendor has MANY services, so there is no single value to
  // order by, and a sort that silently used the first one alphabetically would
  // be a lie the header does not admit to.
  { id: 'services', label: 'Services', defaultOn: true },
  { id: 'activeAgreements', label: 'Active agreements', defaultOn: true },
  { id: 'coiExpiration', label: 'COI expires', defaultOn: true, sortKey: 'coi_expiration' },
  { id: 'status', label: 'Status', defaultOn: true, sortKey: 'status' },
  { id: 'updatedAt', label: 'Last updated', sortKey: 'updated_at' },
];

export const TABLE_CATALOGS = {
  properties: PROPERTY_COLUMNS,
  tenants: TENANT_ORG_COLUMNS,
  vendors: VENDOR_COLUMNS,
} as const satisfies Record<string, readonly TableColumnDef[]>;

export type TableId = keyof typeof TABLE_CATALOGS;

export const TABLE_IDS = Object.keys(TABLE_CATALOGS) as readonly TableId[];

export function isTableId(value: string): value is TableId {
  return Object.hasOwn(TABLE_CATALOGS, value);
}

/**
 * Row density (docs/04 §5). Lives beside the layouts because it is the other
 * half of the same preference: how many rows fit, and which columns they show.
 */
export const densitySchema = z.enum(['comfortable', 'compact']);
export type Density = z.infer<typeof densitySchema>;

/**
 * Theme (docs/04 §1b, the brand standard's dark-mode page). Three values, not
 * two: 'system' is a real choice — "follow whatever this device is set to" —
 * and it is the default, so a person who has never opened the control gets the
 * theme their OS already asked for. Storing only light/dark would force us to
 * guess one at first sign-in and then be wrong for half the company.
 */
export const themeSchema = z.enum(['light', 'dark', 'system']);
export type ThemePreference = z.infer<typeof themeSchema>;

/**
 * How a list is laid out. Deliberately NOT tied to density (ADR-27): density is
 * "how much fits", view is "what shape the records take". Coupling them, as the
 * v1 beta did, means a person cannot ask for dense cards or roomy rows — and it
 * makes the >=30% density target untestable, because the two modes stop being
 * the same table.
 */
export const tableViewSchema = z.enum(['table', 'cards']);
export type TableView = z.infer<typeof tableViewSchema>;

/**
 * How many rows a page holds (X-6.3). Three sizes, not a free number: the value
 * reaches a SQL LIMIT, so the set of legal answers belongs in the contract and
 * not in whatever a client sends. 50 stays the default — nobody's list changes
 * shape because this became configurable.
 */
export const PAGE_SIZES = [25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 50;
export type PageSize = (typeof PAGE_SIZES)[number];

export function isPageSize(value: number): value is PageSize {
  return (PAGE_SIZES as readonly number[]).includes(value);
}

/**
 * Snap any number to a legal page size rather than refusing it.
 *
 * A stored preference outlives the code that wrote it: if PAGE_SIZES ever
 * changes, a 400 here would break the whole list screen for whoever had the
 * retired value, which is a worse answer than showing them 50 rows. Client bugs
 * are caught by the shared constant both sides read.
 */
export function resolvePageSize(value: number | undefined): PageSize {
  return value !== undefined && isPageSize(value) ? value : DEFAULT_PAGE_SIZE;
}

/**
 * Column width in pixels. Bounded because a stored layout is applied to a
 * rendered table: 0 makes a column unreachable and 5000 makes every other
 * column unreachable, and neither is something a user can undo without a
 * working table to undo it in.
 */
export const MIN_COLUMN_WIDTH = 64;
export const MAX_COLUMN_WIDTH = 720;

export const tableLayoutSchema = z.object({
  /**
   * Visible column ids. An EMPTY array is meaningful — "everything off except
   * the locked columns" — which is why an absent layout (fall back to defaults)
   * and an empty one are different things.
   */
  columns: z.array(z.string().min(1).max(64)).max(64),
  /** Absent means the table's default view, which is `table`. */
  view: tableViewSchema.optional(),
  /**
   * Chosen column order, as ids. Absent means catalog order — which is a
   * different statement from "the user dragged the columns back into catalog
   * order", so it is stored explicitly once they touch it. Partial and stale
   * lists are tolerated by `resolveColumnOrder`: a column the order does not
   * mention keeps its catalog position rather than vanishing.
   */
  order: z.array(z.string().min(1).max(64)).max(64).optional(),
  /**
   * Chosen widths in pixels, by column id. Absent, or a column absent from it,
   * means the table sizes that column itself.
   */
  widths: z
    .record(z.string().min(1).max(64), z.number().int().min(MIN_COLUMN_WIDTH).max(MAX_COLUMN_WIDTH))
    .optional(),
  /** Absent means DEFAULT_PAGE_SIZE. */
  pageSize: z.number().int().optional(),
});
export type TableLayout = z.infer<typeof tableLayoutSchema>;

export const savedLayoutsSchema = z.record(z.string(), tableLayoutSchema);
export type SavedLayouts = z.infer<typeof savedLayoutsSchema>;

/**
 * The single resolution rule, used by the API when it validates a write and by
 * the web app when it renders. Unknown ids are dropped rather than rejected so
 * that removing a column from a catalog does not break every stored layout;
 * locked columns are always present; the result is in catalog order.
 */
export function resolveVisibleColumns(
  tableId: TableId,
  saved: TableLayout | undefined,
): readonly string[] {
  const catalog = TABLE_CATALOGS[tableId];
  if (saved === undefined) {
    return catalog.filter((c) => c.locked === true || c.defaultOn === true).map((c) => c.id);
  }
  const chosen = new Set(saved.columns);
  return catalog.filter((c) => c.locked === true || chosen.has(c.id)).map((c) => c.id);
}

/**
 * Visible columns in the order the user chose (X-6.2's sibling: reordering).
 *
 * Built from `resolveVisibleColumns` rather than beside it, so visibility stays
 * one rule with one definition. Anything the stored order does not mention is
 * appended in catalog order — which is what makes a NEW column appear for
 * somebody who reordered six months ago instead of silently disappearing.
 */
export function resolveColumnOrder(
  tableId: TableId,
  saved: TableLayout | undefined,
): readonly string[] {
  const visible = resolveVisibleColumns(tableId, saved);
  if (saved?.order === undefined) return visible;
  const wanted = new Set(visible);
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of saved.order) {
    if (wanted.has(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }
  for (const id of visible) if (!seen.has(id)) ordered.push(id);
  return ordered;
}

/** The stored width for one column, or undefined to let the table size it. */
export function resolveColumnWidth(
  saved: TableLayout | undefined,
  columnId: string,
): number | undefined {
  return saved?.widths?.[columnId];
}

/** The chosen view, or the default for a user who has not chosen one. */
export function resolveView(saved: TableLayout | undefined): TableView {
  return saved?.view ?? 'table';
}

/** True when the layout matches what a user who never chose would see. */
export function isDefaultLayout(tableId: TableId, saved: TableLayout | undefined): boolean {
  const current = resolveVisibleColumns(tableId, saved).join(',');
  return current === resolveVisibleColumns(tableId, undefined).join(',');
}
